import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronRight, Flag, EyeOff, Eye } from 'lucide-react';
import api from './api';
import { keyBetween, compareSortKey } from './sortKey';

// ---------- Утилиты дерева (плоский массив tasks с parentId/sortKey) ----------

export function childrenOf(tasks, parentId, knownIds) {
  const list = tasks.filter(t => {
    if (parentId == null) return !t.parentId || (knownIds && !knownIds.has(t.parentId));
    return t.parentId === parentId;
  });
  return list.sort(compareSortKey);
}

// Плоский видимый список [{task, depth}] с учётом свёрнутых веток и скрытия выполненных
export function flatVisible(tasks, rootId, collapsed, hideCompleted) {
  const knownIds = new Set(tasks.map(t => t.id));
  const rows = [];
  const walk = (parentId, depth) => {
    childrenOf(tasks, parentId, knownIds).forEach(t => {
      if (hideCompleted && t.isDone) return;
      rows.push({ task: t, depth });
      if (!collapsed.has(t.id)) walk(t.id, depth + 1);
    });
  };
  walk(rootId, 0);
  return rows;
}

export function isDescendant(tasks, maybeChildId, ancestorId) {
  const byId = new Map(tasks.map(t => [t.id, t]));
  let cur = byId.get(maybeChildId);
  let guard = 0;
  while (cur && cur.parentId && guard++ < 1000) {
    if (cur.parentId === ancestorId) return true;
    cur = byId.get(cur.parentId);
  }
  return false;
}

// Ключ для вставки сразу ПОСЛЕ afterId среди детей parentId (afterId == null → в конец)
export function keyAfterSibling(tasks, parentId, afterId) {
  const knownIds = new Set(tasks.map(t => t.id));
  const sibs = childrenOf(tasks, parentId, knownIds);
  const idx = afterId != null ? sibs.findIndex(s => s.id === afterId) : sibs.length - 1;
  const prev = idx >= 0 ? sibs[idx] : null;
  const next = idx >= 0 && idx + 1 < sibs.length ? sibs[idx + 1] : null;
  return keyBetween(prev ? prev.sortKey : null, next ? next.sortKey : null);
}

// Ключ для вставки ПЕРЕД beforeId среди детей parentId (исключая excludeId)
export function keyBeforeSibling(tasks, parentId, beforeId, excludeId) {
  const knownIds = new Set(tasks.map(t => t.id));
  const sibs = childrenOf(tasks, parentId, knownIds).filter(s => s.id !== excludeId);
  const idx = sibs.findIndex(s => s.id === beforeId);
  if (idx < 0) return keyBetween(null, null);
  const prev = idx > 0 ? sibs[idx - 1] : null;
  return keyBetween(prev ? prev.sortKey : null, sibs[idx].sortKey);
}

const isTemp = (id) => typeof id === 'string' && id.startsWith('temp-');
const newTempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const PRIORITY_COLORS = { 1: '#006B59', 2: '#2AAE9C', 3: '#7FCFC4', 4: '#adb5bd' };

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const d = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

function formatDue(dueDate) {
  return new Date(dueDate).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function loadLS(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : JSON.parse(v);
  } catch { return fallback; }
}

// ---------- Компонент «Аутлайн» ----------

export default function OutlineView({ tasks, setTasks, onToggleDone, onCyclePriority, reload }) {
  const [path, setPath] = useState([]); // цепочка id для зума
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set(loadLS('terkaOutlineCollapsed', [])));
  const [hideCompleted, setHideCompleted] = useState(() => loadLS('terkaOutlineHideDone', false));
  const [dropId, setDropId] = useState(null);
  const dragIdRef = useRef(null);
  const skipBlurRef = useRef(false); // клавиша уже обработала commit — blur не дублирует
  const byId = new Map(tasks.map(t => [t.id, t]));

  const currentRootId = path.length > 0 ? path[path.length - 1] : null;
  const rows = flatVisible(tasks, currentRootId, collapsed, hideCompleted);

  useEffect(() => {
    localStorage.setItem('terkaOutlineCollapsed', JSON.stringify([...collapsed]));
  }, [collapsed]);
  useEffect(() => {
    localStorage.setItem('terkaOutlineHideDone', JSON.stringify(hideCompleted));
  }, [hideCompleted]);

  // Esc вне редактирования — на уровень вверх по крошкам
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape' && editingId == null && path.length > 0) {
        setPath(p => p.slice(0, -1));
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [editingId, path.length]);

  const toggleCollapse = (id) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const startEdit = (task) => { setEditingId(task.id); setEditText(task.title || ''); };
  const stopEdit = () => { setEditingId(null); setEditText(''); };

  // Сохранение названия. Temp без текста — удаляем; temp с текстом — POST; реальная — PUT.
  const commitTitle = useCallback((task, text) => {
    const title = (text != null ? text : '').trim();
    if (isTemp(task.id)) {
      if (!title) {
        setTasks(prev => prev.filter(t => t.id !== task.id));
        return;
      }
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, title } : t));
      api.post('/tasks', { title, parentId: task.parentId || null, sortKey: task.sortKey })
        .then(({ data }) => {
          if (data && data.task) {
            setTasks(prev => prev.map(t => t.id === task.id
              ? { ...data.task, sortKey: data.task.sortKey || task.sortKey, parentId: data.task.parentId ?? task.parentId ?? null }
              : t));
            // Если фокус ещё на temp-строке — переносим на реальный id
            setEditingId(prev => (prev === task.id ? data.task.id : prev));
          }
        })
        .catch(() => setTasks(prev => prev.filter(t => t.id !== task.id)));
      return;
    }
    if (title && title !== task.title) {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, title } : t));
      api.put(`/tasks/${task.id}`, { title }).catch(() => reload());
    }
  }, [setTasks, reload]);

  // «+ Новая задача» — пустая temp-строка в КОНЕЦ сиблингов текущего зум-уровня
  const createAtEnd = useCallback(() => {
    const parentId = currentRootId || null;
    const sortKey = keyAfterSibling(tasks, parentId, null);
    const temp = {
      id: newTempId(), title: '', parentId, sortKey,
      isDone: false, priority: 4, dueDate: null, attachments: [],
    };
    setTasks(prev => [...prev, temp]);
    setEditingId(temp.id);
    setEditText('');
  }, [tasks, currentRootId, setTasks]);

  // Пустой список: автофокус в строку-плейсхолдер (один раз на «опустение» —
  // Esc убирает пустышку и не зацикливает пересоздание)
  const autoDraftRef = useRef(false);
  const isEmpty = rows.length === 0;
  const hasRealRows = rows.some(r => !isTemp(r.task.id));
  useEffect(() => {
    // Сбрасываем флаг только когда появились реальные задачи — иначе Esc
    // на автодрафте зациклил бы его пересоздание.
    if (hasRealRows) { autoDraftRef.current = false; }
    if (!isEmpty || autoDraftRef.current) return;
    autoDraftRef.current = true;
    createAtEnd();
  }, [isEmpty, hasRealRows, createAtEnd]);

  // Enter — новая пустая строка сразу под текущей, тот же уровень, сразу в режиме ввода
  const createAfter = (task) => {
    const parentId = task.parentId || (currentRootId || null);
    const sortKey = keyAfterSibling(tasks, parentId, task.id);
    const temp = {
      id: newTempId(), title: '', parentId, sortKey,
      isDone: false, priority: 4, dueDate: null, attachments: [],
    };
    setTasks(prev => [...prev, temp]);
    setEditingId(temp.id);
    setEditText('');
  };

  const sendReorder = (id, parentId, sortKey) => {
    if (isTemp(id)) return; // temp живёт только локально до первого сохранения
    api.post('/tasks/reorder', { items: [{ id, parentId: parentId || null, sortKey }] })
      .catch(() => reload());
  };

  // Tab — подзадача предыдущего сиблинга (optimistic + reorder)
  const indent = (task, text) => {
    const parentId = task.parentId || (currentRootId || null);
    const knownIds = new Set(tasks.map(t => t.id));
    const sibs = childrenOf(tasks, parentId, knownIds);
    const idx = sibs.findIndex(s => s.id === task.id);
    if (idx <= 0) return;
    const newParent = sibs[idx - 1];
    if (isTemp(newParent.id)) return;
    const sortKey = keyAfterSibling(tasks, newParent.id, null);
    setTasks(prev => prev.map(t => t.id === task.id
      ? { ...t, title: (text != null ? text : t.title), parentId: newParent.id, sortKey }
      : t));
    setCollapsed(prev => { const n = new Set(prev); n.delete(newParent.id); return n; });
    sendReorder(task.id, newParent.id, sortKey);
  };

  // Shift+Tab — на уровень выше, сразу после бывшего родителя
  const outdent = (task, text) => {
    const parent = task.parentId ? byId.get(task.parentId) : null;
    if (!parent) return;
    if (currentRootId && parent.id === currentRootId) return; // из зум-корня не выносим
    const grandId = parent.parentId || null;
    const sortKey = keyAfterSibling(tasks, grandId, parent.id);
    setTasks(prev => prev.map(t => t.id === task.id
      ? { ...t, title: (text != null ? text : t.title), parentId: grandId, sortKey }
      : t));
    sendReorder(task.id, grandId, sortKey);
  };

  // Backspace на пустой строке — удалить, курсор в предыдущую видимую
  const deleteEmpty = (task) => {
    const idx = rows.findIndex(r => r.task.id === task.id);
    const prevRow = idx > 0 ? rows[idx - 1].task : null;
    setTasks(prev => prev.filter(t => t.id !== task.id));
    if (!isTemp(task.id)) api.delete(`/tasks/${task.id}`).catch(() => reload());
    if (prevRow) { setEditingId(prevRow.id); setEditText(prevRow.title || ''); }
    else stopEdit();
  };

  const moveEdit = (task, dir) => {
    commitTitle(task, editText);
    const idx = rows.findIndex(r => r.task.id === task.id);
    const next = rows[idx + (dir === 'down' ? 1 : -1)];
    if (next) { setEditingId(next.task.id); setEditText(next.task.title || ''); }
  };

  const handleKeyDown = (e, task) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (!isTemp(task.id)) onToggleDone(task); // зачёркивается на месте
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      skipBlurRef.current = true;
      commitTitle(task, editText);
      if (editText.trim() || !isTemp(task.id)) createAfter(task);
      else stopEdit();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) outdent(task, editText); else indent(task, editText);
      return;
    }
    if (e.key === 'Backspace' && editText === '') {
      e.preventDefault();
      skipBlurRef.current = true;
      deleteEmpty(task);
      return;
    }
    if (e.key === 'ArrowUp') { e.preventDefault(); skipBlurRef.current = true; moveEdit(task, 'up'); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); skipBlurRef.current = true; moveEdit(task, 'down'); return; }
    if (e.key === 'Escape') { skipBlurRef.current = true; commitTitle(task, editText); stopEdit(); }
  };

  // --- Drag & drop: бросаем ПЕРЕД целевой строкой, на её уровень ---
  const onDragStart = (e, task) => {
    dragIdRef.current = task.id;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(task.id)); } catch {}
  };
  const onDragOver = (e, task) => {
    const dragId = dragIdRef.current;
    if (dragId == null || dragId === task.id) return;
    if (isDescendant(tasks, task.id, dragId)) return; // нельзя в собственное поддерево
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropId(task.id);
  };
  const onDrop = (e, target) => {
    e.preventDefault();
    const dragId = dragIdRef.current;
    dragIdRef.current = null;
    setDropId(null);
    if (dragId == null || dragId === target.id) return;
    if (isDescendant(tasks, target.id, dragId)) return;
    const parentId = target.parentId || (currentRootId || null);
    const sortKey = keyBeforeSibling(tasks, parentId, target.id, dragId);
    setTasks(prev => prev.map(t => t.id === dragId ? { ...t, parentId, sortKey } : t));
    sendReorder(dragId, parentId, sortKey);
  };
  const onDragEnd = () => { dragIdRef.current = null; setDropId(null); };

  const breadcrumbs = path.map(id => ({ id, title: (byId.get(id) || {}).title || 'Без названия' }));
  const childCount = (id) => tasks.filter(t => t.parentId === id).length;

  return (
    <div>
      {/* Панель: крошки + тумблер выполненных */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
        borderBottom: '1px solid #f1f3f5', fontSize: 12, color: '#888',
        position: 'sticky', top: 0, background: 'white', zIndex: 5,
      }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', whiteSpace: 'nowrap' }}>
          <button
            onClick={() => setPath([])}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: path.length ? '#7B2FF7' : '#495057', fontWeight: 600 }}
          >
            Все задачи
          </button>
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={b.id}>
              <span style={{ color: '#ced4da' }}>›</span>
              <button
                onClick={() => setPath(prev => prev.slice(0, i + 1))}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12,
                  color: i === breadcrumbs.length - 1 ? '#495057' : '#7B2FF7',
                  fontWeight: i === breadcrumbs.length - 1 ? 600 : 400,
                  overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110,
                }}
              >
                {b.title}
              </button>
            </React.Fragment>
          ))}
        </div>
        <button
          onClick={() => setHideCompleted(v => !v)}
          title={hideCompleted ? 'Показать выполненные' : 'Скрывать выполненные'}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, border: '1px solid #e1e5e9', borderRadius: 12,
            background: hideCompleted ? '#e0f5f1' : 'white', color: hideCompleted ? '#006B59' : '#888',
            fontSize: 11, padding: '3px 8px', cursor: 'pointer', flexShrink: 0,
          }}
        >
          {hideCompleted ? <EyeOff size={12} /> : <Eye size={12} />}
          выполненные
        </button>
      </div>

      {/* Строки аутлайна */}
      <div style={{ padding: '6px 8px' }}>
        {rows.length === 0 && (
          <div
            onClick={createAtEnd}
            style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 13, cursor: 'text' }}
          >
            Нет задач. Начни печатать…
          </div>
        )}
        {rows.map(({ task, depth }) => {
          const isEditing = editingId === task.id;
          const hasChildren = childCount(task.id) > 0;
          const color = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS[4];
          const overdue = !task.isDone && isOverdue(task.dueDate);
          return (
            <div
              key={task.id}
              draggable={!isEditing && !isTemp(task.id)}
              onDragStart={(e) => onDragStart(e, task)}
              onDragOver={(e) => onDragOver(e, task)}
              onDrop={(e) => onDrop(e, task)}
              onDragEnd={onDragEnd}
              style={{
                display: 'flex', alignItems: 'center', gap: 2,
                paddingLeft: 4 + depth * 20, paddingRight: 6,
                minHeight: 30, borderRadius: 6,
                borderTop: dropId === task.id ? '2px solid #2AAE9C' : '2px solid transparent',
                background: isEditing ? '#f8f5ff' : 'transparent',
              }}
            >
              {/* Треугольник сворачивания */}
              <button
                onClick={() => hasChildren && toggleCollapse(task.id)}
                style={{
                  width: 18, height: 18, border: 'none', background: 'transparent', padding: 0,
                  cursor: hasChildren ? 'pointer' : 'default', color: '#adb5bd', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  visibility: hasChildren ? 'visible' : 'hidden',
                }}
              >
                <ChevronRight size={12} style={{ transform: collapsed.has(task.id) ? 'none' : 'rotate(90deg)', transition: 'transform 0.12s' }} />
              </button>

              {/* Буллет — зум в ветку */}
              <button
                onClick={() => { if (!isTemp(task.id)) { setPath(prev => [...prev, task.id]); stopEdit(); } }}
                title="Открыть ветку"
                style={{
                  width: 18, height: 18, border: 'none', background: 'transparent', padding: 0,
                  cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: task.isDone ? '#ced4da' : '#495057',
                  boxShadow: collapsed.has(task.id) && hasChildren ? '0 0 0 3px #e9ecef' : 'none',
                }} />
              </button>

              {/* Название: inline-редактирование */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                  <input
                    autoFocus
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, task)}
                    onBlur={() => {
                      if (skipBlurRef.current) { skipBlurRef.current = false; return; }
                      commitTitle(task, editText);
                      stopEdit();
                    }}
                    onFocus={(e) => { const l = e.target.value.length; e.target.setSelectionRange(l, l); }}
                    placeholder="Новая задача…"
                    style={{
                      width: '100%', border: 'none', outline: 'none', background: 'transparent',
                      fontSize: 14, padding: '4px 0', color: '#333', boxSizing: 'border-box',
                    }}
                  />
                ) : (
                  <div
                    onClick={() => startEdit(task)}
                    style={{
                      fontSize: 14, padding: '4px 0', cursor: 'text',
                      color: task.isDone ? '#adb5bd' : '#333',
                      textDecoration: task.isDone ? 'line-through' : 'none',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {task.title || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>Без названия</span>}
                  </div>
                )}
              </div>

              {/* Чипы: дата + приоритет */}
              {task.dueDate && (
                <span style={{
                  fontSize: 11, flexShrink: 0, padding: '1px 6px', borderRadius: 8,
                  background: overdue ? '#fdecea' : '#f1f3f5',
                  color: overdue ? '#dc3545' : '#888',
                  fontWeight: overdue ? 600 : 400,
                }}>
                  {formatDue(task.dueDate)}
                </span>
              )}
              <button
                onMouseDown={(e) => e.preventDefault() /* не терять фокус ввода */}
                onClick={() => !isTemp(task.id) && onCyclePriority(task)}
                title={`Приоритет P${task.priority} — клик меняет`}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0,
                  width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color, padding: 0,
                }}
              >
                <Flag size={12} fill={task.priority < 4 ? color : 'none'} />
              </button>
            </div>
          );
        })}
        {/* Постоянная строка-плейсхолдер: новая задача в конец текущего уровня */}
        <div
          onClick={createAtEnd}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            paddingLeft: 40, paddingRight: 6, minHeight: 30,
            color: '#adb5bd', fontSize: 14, cursor: 'text', userSelect: 'none',
          }}
        >
          <span style={{ fontSize: 15, lineHeight: 1 }}>+</span>
          <span>Новая задача</span>
        </div>
      </div>
    </div>
  );
}
